// src/services/api.js
import axios from 'axios'

// Utiliser la variable d'environnement ou une valeur par défaut
const API_URL = 'https://api.opsolver.cloud/'

const api = axios.create({
  baseURL: API_URL
})

// Intercepteur pour ajouter automatiquement le token
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
}, error => {
  return Promise.reject(error)
})

// Intercepteur de réponse pour gérer les erreurs d'authentification
api.interceptors.response.use(response => {
  return response
}, error => {
  if (error.response && error.response.status === 401) {
    // Rediriger vers la page de login si le token est invalide
    localStorage.removeItem('token')
    window.location.href = '/login'
  }
  return Promise.reject(error)
})

// Méthodes API utilitaires
const apiMethods = {

  getApiUrl() {
    return `${API_URL}/data/uploads/`
  },
  // Authentification
  login(credentials) {
    return api.post('/api/auth/login', credentials)
  },
  
  changePassword(passwordData) {
    return api.put('/api/auth/change-password', passwordData)
  },
  
  // Personnel
  getPersonnelList(page = 1, limit = 10, search = '') {
    return api.get(`/api/personnel?page=${page}&limit=${limit}&search=${search}`)
  },

  getPointagesByAgentId(agentId, month) {
    //"Mois au format YYYY-MM"
    return api.get(`/api/personnel/${agentId}/pointages?month=${month}`)
  },
  
  getPersonnelByDirection() {
    return api.get('/api/personnel/count-by-direction')
  },
  
  getPersonnelByGrade() {
    return api.get('/api/personnel/count-by-grade')
  },
  
  getAgentById(id) {
    return api.get(`/api/personnel/${id}`)
  },

  createAgent(agentData) {
    return api.post('/api/personnel', agentData)
  },

  updateAgent(id, agentData) {
    return api.put(`/api/personnel/${id}`, agentData)
  },
  
  deleteAgent(id) {
    return api.delete(`/api/personnel/${id}`)
  },

  updateAgentPhoto(id, photoData) {
    return api.put(`/api/personnel/${id}/photo`, { photo: photoData })
  },
  
  // Pointage
  getPointagesToday() {
    const today = new Date().toLocaleDateString('fr-CA');
    return api.get(`/api/admin/stats?date=${today}`)
  },
  
  // Directions - Gestion des paramètres
  getDirections() {
    return api.get('/api/parametres/directions')
  },
  
  getDirection(id) {
    return api.get(`/api/parametres/directions/${id}`)
  },
  
  createDirection(directionData) {
    return api.post('/api/parametres/directions', directionData)
  },
  
  updateDirection(id, directionData) {
    return api.put(`/api/parametres/directions/${id}`, directionData)
  },
  
  deleteDirection(id) {
    return api.delete(`/api/parametres/directions/${id}`)
  },
  
  // Pays - Gestion des paramètres
  getPays() {
    return api.get('/api/parametres/countries')
  },

  getPaysById(id) {
    return api.get(`/api/parametres/countries/${id}`)
  },

  createPays(paysData) {
    return api.post('/api/parametres/countries', paysData)
  },

  updatePays(id, paysData) {
    return api.put(`/api/parametres/countries/${id}`, paysData)
  },

  deletePays(id) {
    return api.delete(`/api/parametres/countries/${id}`)
  },

  // Villes - Gestion des paramètres
  getVilles() {
    return api.get('/api/parametres/villes')
  },

  getVilleById(id) {
    return api.get(`/api/parametres/villes/${id}`)
  },

  createVille(villeData) {
    return api.post('/api/parametres/villes', villeData)
  },

  updateVille(id, villeData) {
    return api.put(`/api/parametres/villes/${id}`, villeData)
  },

  deleteVille(id) {
    return api.delete(`/api/parametres/villes/${id}`)
  },

  // Provinces - Gestion des paramètres
  getProvinces() {
    return api.get('/api/parametres/provinces')
  },

  createProvince(provinceData) {
    return api.post('/api/parametres/provinces', provinceData)
  },

  updateProvince(id, provinceData) {
    return api.put(`/api/parametres/provinces/${id}`, provinceData)
  },

  deleteProvince(id) {
    return api.delete(`/api/parametres/provinces/${id}`)
  },

  // Jours non-ouvrables - Gestion des paramètres
  getJoursNonOuvrables(params = {}) {
    const queryParams = new URLSearchParams()
    
    if (params && params.annee) queryParams.append('annee', params.annee)
    
    return api.get(`/api/parametres/jours-non-ouvrables?${queryParams.toString()}`)
  },

  createJourNonOuvrable(jourData) {
    console.log(jourData);
    return api.post('/api/parametres/jours-non-ouvrables', jourData);
  },

  updateJourNonOuvrable(id, jourData) {
    return api.put(`/api/parametres/jours-non-ouvrables/${id}`, jourData);
  },

  deleteJourNonOuvrable(id) {
    return api.delete(`/api/parametres/jours-non-ouvrables/${id}`);
  },

  // Cette section est déjà définie plus haut
  getDirectionById(id) {
    return api.get(`/api/directions/${id}`)
  },

  // Grades - Gestion des paramètres
  getGrades() {
    return api.get('/api/parametres/grades')
  },

  getGradeById(id) {
    return api.get(`/api/parametres/grades/${id}`)
  },
  
  getAgentsByGrade(gradeId) {
    return api.get(`/api/parametres/grades/${gradeId}/agents`)
  },

  createGrade(gradeData) {
    return api.post('/api/parametres/grades', gradeData)
  },

  updateGrade(id, gradeData) {
    return api.put(`/api/parametres/grades/${id}`, gradeData)
  },

  deleteGrade(id) {
    return api.delete(`/api/parametres/grades/${id}`)
  },

  // Types de congés - Gestion des paramètres
  getCongesTypes() {
    return api.get('/api/parametres/types-conges')
  },

  getCongeTypeById(id) {
    return api.get(`/api/parametres/types-conges/${id}`)
  },

  createCongeType(congeData) {
    return api.post('/api/parametres/types-conges', congeData)
  },

  updateCongeType(id, congeData) {
    return api.put(`/api/parametres/types-conges/${id}`, congeData)
  },

  deleteCongeType(id) {
    return api.delete(`/api/parametres/types-conges/${id}`)
  },

  // Fonctions - Gestion des fonctions professionnelles
  getFonctions() {
    return api.get('/api/parametres/fonctions')
  },

  getFonctionById(id) {
    return api.get(`/api/parametres/fonctions/${id}`)
  },

  createFonction(fonctionData) {
    return api.post('/api/parametres/fonctions', fonctionData)
  },

  updateFonction(id, fonctionData) {
    return api.put(`/api/parametres/fonctions/${id}`, fonctionData)
  },

  deleteFonction(id) {
    return api.delete(`/api/parametres/fonctions/${id}`)
  },

  // Récupérer les agents par fonction
  getAgentsByFonction(fonctionId) {
    return api.get(`/api/parametres/fonctions/${fonctionId}/agents`)
  },

  // Sites de Pointage - Gestion des sites
  getSites() {
    return api.get('/api/sites')
  },
  
  getSitesByVilleId(villeId) {
    return api.get(`/api/parametres/villes/${villeId}/sites`)
  },
  
  getSiteById(id) {
    return api.get(`/api/sites/${id}`)
  },
  
  createSite(siteData) {
    return api.post('/api/sites', siteData)
  },
  
  updateSite(id, siteData) {
    console.log(siteData, id);
    return api.put(`/api/sites/${id}`, siteData)
  },
  
  deleteSite(id) {
    return api.delete(`/api/sites/${id}`)
  },



  getPointageByAgent(agentId, startDate, endDate) {
    return api.get(`/api/pointage/agent/${agentId}?start=${startDate}&end=${endDate}`)
  },
  
  // Statistiques
  getDashboardStats() {
    return api.get('/api/admin/stats')
  },

  // Shifts - Gestion des quarts de travail
  getShifts() {
    return api.get('/api/parametres/shifts')
  },

  getShiftById(id) {
    return api.get(`/api/parametres/shifts/${id}`)
  },

  createShift(shiftData) {
    return api.post('/api/parametres/shifts', shiftData)
  },

  updateShift(id, shiftData) {
    return api.put(`/api/parametres/shifts/${id}`, shiftData)
  },

  deleteShift(id) {
    return api.delete(`/api/parametres/shifts/${id}`)
  },

  // Pointages liés aux shifts
  getPointagesByShift(shiftId, params = {}) {
    return api.get(`/api/pointage/shift/${shiftId}`, { params })
  },

  getShiftStats(shiftId, debut, fin) {
    return api.get(`/api/pointage/shift/${shiftId}/stats`, {
      params: { debut, fin }
    })
  },

  validatePointage(pointageData) {
    return api.post('/api/pointage/validate', pointageData)
  },

  // Congés - Gestion des congés
  getConges(params = {}) {
    const queryParams = new URLSearchParams()
    
    // Ajouter les paramètres de pagination
    if (params.page) queryParams.append('page', params.page)
    if (params.limit) queryParams.append('limit', params.limit)
    
    // Ajouter les paramètres de tri
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)
    
    // Ajouter les paramètres de filtrage
    if (params.agentId) queryParams.append('agentId', params.agentId)
    if (params.typeConge) queryParams.append('typeConge', params.typeConge)
    if (params.statut) queryParams.append('statut', params.statut)
    if (params.dateDebut) queryParams.append('dateDebut', params.dateDebut)
    if (params.dateFin) queryParams.append('dateFin', params.dateFin)
    
    // Ajouter les paramètres d'inclusion
    if (params.withApprobations !== undefined) queryParams.append('withApprobations', params.withApprobations)
    if (params.withQuotas !== undefined) queryParams.append('withQuotas', params.withQuotas)
    
    return api.get(`/api/conges?${queryParams.toString()}`)
  },
  
  getCongeById(id, params = {}) {
    const queryParams = new URLSearchParams()
    
    // Ajouter les paramètres d'inclusion
    if (params.withApprobations !== undefined) queryParams.append('withApprobations', params.withApprobations)
    
    return api.get(`/api/conges/${id}?${queryParams.toString()}`)
  },
  
  createConge(congeData) {
    return api.post('/api/conges', congeData)
  },
  
  updateConge(id, congeData) {
    return api.put(`/api/conges/${id}`, congeData)
  },
  
  deleteConge(id) {
    return api.delete(`/api/conges/${id}`)
  },
  
  changeCongeStatus(id, statusData) {
    return api.put(`/api/conges/${id}/statut`, statusData)
  },
  
  // Quotas de congés
  getCongeQuotas(params = {}) {
    const queryParams = new URLSearchParams()
    
    // Ajouter les paramètres de pagination
    if (params.page) queryParams.append('page', params.page)
    if (params.limit) queryParams.append('limit', params.limit)
    
    // Ajouter les paramètres de filtrage
    if (params.personnelId) queryParams.append('personnelId', params.personnelId)
    if (params.typeCongeId) queryParams.append('typeCongeId', params.typeCongeId)
    if (params.annee) queryParams.append('annee', params.annee)
    
    // Ajouter les paramètres de tri
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)
    
    return api.get(`/api/conges/quotas?${queryParams.toString()}`)
  },
  
  getCongeQuota(personnelId, typeCongeId, annee) {
    return api.get(`/api/conges/quotas/${personnelId}/${typeCongeId}/${annee}`)
  },
  
  createCongeQuota(quotaData) {
    return api.post('/api/conges/quotas', quotaData)
  },
  
  updateCongeQuota(id, quotaData) {
    return api.put(`/api/conges/quotas/${id}`, quotaData)
  },
  
  deleteCongeQuota(id) {
    return api.delete(`/api/conges/quotas/${id}`)
  },
  
  exportCongeQuotas(format = 'csv', filters = {}) {
    const queryParams = new URLSearchParams()
    
    // Ajouter les paramètres de filtrage
    if (filters.personnelId) queryParams.append('personnelId', filters.personnelId)
    if (filters.typeCongeId) queryParams.append('typeCongeId', filters.typeCongeId)
    if (filters.annee) queryParams.append('annee', filters.annee)
    
    // Définir les options pour le téléchargement du fichier
    return api.get(`/api/conges/quotas/export/${format}?${queryParams.toString()}`, {
      responseType: 'blob'
    })
  },
  
  // Pièces justificatives
  uploadJustificatif(congeId, file) {
    const formData = new FormData()
    formData.append('file', file)
    
    return api.post(`/api/conges/${congeId}/justificatif`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  
  getJustificatif(congeId) {
    return api.get(`/api/conges/${congeId}/justificatif`)
  },
  
  // Méthodes pour le calendrier des congés
  getCongesForCalendar(params = {}) {
    const queryParams = new URLSearchParams()
    
    if (params.dateDebut) queryParams.append('dateDebut', params.dateDebut)
    if (params.dateFin) queryParams.append('dateFin', params.dateFin)
    if (params.directionId) queryParams.append('directionId', params.directionId)
    if (params.typeCongeId) queryParams.append('typeCongeId', params.typeCongeId)
    
    return api.get(`/api/conges/calendar?${queryParams.toString()}`)
  },
  
  deleteJustificatif(congeId) {
    return api.delete(`/api/conges/${congeId}/justificatif`)
  },
}

// Attacher les méthodes utilitaires à l'instance API
Object.assign(api, apiMethods)

export default api